import React, { useMemo } from 'react';
import {
  ComposedChart,
  Line,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { 
  transformDuplicatesDriftForChart
} from '../../../../utils/qaqc/chartDataTransform';
import {
  computeRpdDomainMax,
  augmentRowsRpdPlot,
  augmentRollingRpdPlot
} from '../../../../utils/qaqc/chartOutliers';
import { PRINT_COLORS, PRINT_CHART_CONFIG } from '../printTheme';
import PrintChartLegendContent from '../PrintChartLegendContent';

const PrintDuplicatesChart = ({ data, element }) => {
  // Transform data for chart
  const chartData = useMemo(() => {
    return transformDuplicatesDriftForChart(data, element);
  }, [data, element]);

  // Calculate statistics
  const stats = useMemo(() => {
    if (chartData.length === 0) return null;
    
    const failures = chartData.filter(d => d.status === 'fail').length;
    const warnings = chartData.filter(d => d.status === 'warning').length;
    const passes = chartData.filter(d => d.status === 'pass').length;
    
    const rpdValues = chartData.map(d => d.rpdPct).filter(v => v !== null && !isNaN(v));
    const meanRPD = rpdValues.length > 0 
      ? (rpdValues.reduce((sum, v) => sum + v, 0) / rpdValues.length).toFixed(2)
      : 'N/A';
    
    return {
      count: chartData.length,
      failures,
      warnings,
      passes,
      passRate: ((passes / chartData.length) * 100).toFixed(1),
      meanRPD
    };
  }, [chartData]);

  const rpdBundle = useMemo(() => {
    if (chartData.length === 0) return { rows: [], yDomain: [0, 40] };
    const rpdVals = chartData
      .map((d) => d.rpdPct)
      .filter((v) => v != null && !Number.isNaN(v));
    const yMax = computeRpdDomainMax(rpdVals, 40);
    let rows = augmentRowsRpdPlot(chartData, 'rpdPct', yMax);
    rows = augmentRollingRpdPlot(rows, 'rollingMeanRPD50', yMax);
    const vals = rows
      .map((d) => d.plotRpdPct)
      .filter((v) => typeof v === 'number' && !Number.isNaN(v));
    const hi = Math.max(yMax, ...(vals.length ? vals : [0]));
    const pad = hi * 0.02 || 1;
    return { rows, yDomain: [0, hi + pad] };
  }, [chartData]);

  const rpdScatterShape = (props) => {
    const { cx, cy, payload } = props;
    if (!payload || cx == null || cy == null) return null;
    const clip = payload.rpdAxisLabel;
    const hi =
      clip && payload.rpdPct > (payload.plotRpdPct ?? payload.rpdPct);
    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r={4}
          fill={PRINT_COLORS.scatter}
          fillOpacity={0.65}
        />
        {clip && (
          <text
            x={cx}
            y={hi ? cy + 11 : cy - 8}
            fill={PRINT_COLORS.text}
            fontSize={7}
            textAnchor="middle"
          >
            {clip}
          </text>
        )}
      </g>
    );
  };

  if (chartData.length === 0) {
    return (
      <div style={{ width: '100%', padding: '20px', background: PRINT_COLORS.background }}>
        <h3 style={{ color: PRINT_COLORS.text, textAlign: 'center' }}>
          Duplicates Precision Analysis - {element}
        </h3>
        <p style={{ color: PRINT_COLORS.text, textAlign: 'center' }}>No data available</p>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', padding: '20px', background: PRINT_COLORS.background }}>
      <div style={{ marginBottom: '15px' }}>
        <h3 style={{ color: PRINT_COLORS.text, marginBottom: '5px', fontSize: '16px', fontWeight: 'bold' }}>
          Duplicates Precision Analysis - {element}
        </h3>
        {stats && (
          <div style={{ 
            display: 'flex', 
            gap: '20px', 
            fontSize: '10px', 
            color: PRINT_COLORS.text,
            borderBottom: `1px solid ${PRINT_COLORS.border}`,
            paddingBottom: '8px'
          }}>
            <span><strong>Total:</strong> {stats.count}</span>
            <span><strong>Mean RPD:</strong> {stats.meanRPD}%</span>
            <span style={{ color: PRINT_COLORS.pass }}><strong>Pass:</strong> {stats.passes} ({stats.passRate}%)</span>
            <span style={{ color: PRINT_COLORS.warning }}><strong>Warning:</strong> {stats.warnings}</span>
            <span style={{ color: PRINT_COLORS.fail }}><strong>Fail:</strong> {stats.failures}</span>
          </div>
        )}
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart 
          data={rpdBundle.rows} 
          margin={PRINT_CHART_CONFIG.margin}
        >
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke={PRINT_COLORS.grid} 
          />
          
          <XAxis 
            dataKey="index" 
            stroke={PRINT_COLORS.text}
            tick={{ fill: PRINT_COLORS.text, fontSize: PRINT_CHART_CONFIG.fontSize.tick }}
            height={56}
            label={{ 
              value: 'Sample Order (by Date)', 
              position: 'insideBottom', 
              offset: 20,
              style: { fill: PRINT_COLORS.text, fontSize: PRINT_CHART_CONFIG.fontSize.axis, fontWeight: 600 }
            }}
          />
          
          <YAxis 
            stroke={PRINT_COLORS.text}
            tick={{ fill: PRINT_COLORS.text, fontSize: PRINT_CHART_CONFIG.fontSize.tick }}
            label={{ 
              value: 'RPD (%)', 
              angle: -90, 
              position: 'insideLeft',
              style: { fill: PRINT_COLORS.text, fontSize: PRINT_CHART_CONFIG.fontSize.axis }
            }}
            domain={rpdBundle.yDomain}
            allowDataOverflow
          />
          
          {/* Reference lines for typical RPD thresholds */}
          <ReferenceLine 
            y={10} 
            stroke={PRINT_COLORS.referenceLineWarn} 
            strokeDasharray="5 5"
            label={{ value: '10% (Warning)', fill: PRINT_COLORS.warning, fontSize: 8, position: 'right' }}
          />
          <ReferenceLine 
            y={20} 
            stroke={PRINT_COLORS.referenceLineFail} 
            strokeDasharray="3 3"
            strokeWidth={PRINT_CHART_CONFIG.strokeWidth.thick}
            label={{ value: '20% (Fail)', fill: PRINT_COLORS.fail, fontSize: 8, position: 'right' }}
          />
          
          {/* RPD scatter - render first (behind) */}
          <Scatter 
            dataKey="plotRpdPct" 
            fill={PRINT_COLORS.scatter}
            fillOpacity={0.55}
            shape={rpdScatterShape}
            name="RPD %"
          />
          
          <Line 
            type="monotone" 
            dataKey="plotRollingMeanRPD50" 
            stroke={PRINT_COLORS.ewma} 
            strokeWidth={PRINT_CHART_CONFIG.strokeWidth.thick}
            strokeOpacity={0.85}
            dot={false}
            connectNulls
            name="Rolling mean RPD"
          />
          
          <Legend content={<PrintChartLegendContent />} />
        </ComposedChart>
      </ResponsiveContainer>

      <div style={{ 
        marginTop: '2px', 
        fontSize: '8px', 
        color: PRINT_COLORS.text,
        fontStyle: 'italic'
      }}>
        Relative Percent Difference (RPD) measures analytical precision. 
        Lower RPD indicates better reproducibility between duplicate samples.
      </div>
    </div>
  );
};

export default PrintDuplicatesChart;
